import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { Grid } from 'module/ui/components/atoms/grid/grid';
import { withEditingMask } from 'module/ui/components/atoms/grid/plugins/with-editing-mask';
import { withExternalFilter } from 'module/ui/components/atoms/grid/plugins/with-external-filter';
import { withSingleClickEdit } from 'module/ui/components/atoms/grid/plugins/with-single-click-edit';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon } from 'module/ui/components/templates/argon';
import { faBuildings } from '@fortawesome/pro-solid-svg-icons';
import { faPlus, faFilter } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { useCompanyFieldsAsFilterableField } from 'helpers/fields/company-fields/fields';
import { keyBy } from 'lodash';
import { pipe } from 'ramda';
import { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';
import { useGridConfiguration } from 'components/organisms/accounts/lists/gridconfig/use-grid-configuration';
import { ViewSwitcher } from 'components/pages/accounts/view-switcher';

const StyledGrid = styled(Grid)`
  .ag-row {
    border-color: var(--table-border-color);
  }
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
  }
  .ag-pinned-left-cols-container {
    padding: 0 !important;
  }
  .ag-cell-last-left-pinned > .ag-react-container {
    align-self: stretch;
  }

  .ag-react-container {
    max-width: 100%;
  }
`;

export const AccountsGrid = pipe(
  pipe(
    () => StyledGrid,
    withExternalFilter,
    withSingleClickEdit,
    withEditingMask,
  ),
)();

export const AccountsUI = () => {
  const { fields: companyFields, loading: isFieldLoading } =
    useCompanyFieldsAsFilterableField();

  return (
    <AccountsList fields={companyFields} isFieldsLoading={isFieldLoading} />
  );
};

export const Wrapper = styled.div`
  position: relative;
  padding-left: var(--left-spacing);
  height: 100%;
`;

const PrimaryFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
`;

const CreateAccountButton = styled(Button)`
  margin-left: var(--spacing-04);
`;

function AccountsList({
  fields: accountFields,
  isFieldsLoading,
}: {
  fields: FilterableField[];
  isFieldsLoading: boolean;
}) {
  useMemo(() => keyBy(accountFields, 'fieldName'), [accountFields]);

  const push = useNavigate();

  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();

  const searchInputRef = useRef<ExpandableSearchInputRef>(null);

  const onCustomerNameClicked = useCallback(
    (data: any) => {
      push(
        `/${ROUTE_PATHS.ACCOUNTS}/${ROUTE_PATHS.ACCOUNT_DETAIL.replace(':accountId', data.companyId.toString())}/${ROUTE_PATHS.ACCOUNT_DETAIL_TIMELINE}`,
      );
    },
    [push],
  );

  const customerNameClickHandlerRef = useRef(onCustomerNameClicked);

  customerNameClickHandlerRef.current = onCustomerNameClicked;

  const { onGridReady, gridOptions } = useGridConfiguration({
    fields: accountFields,
    isFieldsLoading,
    customerNameClickHandler: useCallback(
      (data: any) => customerNameClickHandlerRef.current(data),
      [],
    ),
    //filter: urlParams.filter,
    searchText: searchQuery,
  });

  return (
    <Argon
      actions={{
        primary: (
          <PrimaryFilterContainer>
            <ViewSwitcher
              primaryPageUrl={'/accounts'}
              secondaryPageUrl={'/accounts/list'}
            />
            <Button
              variant="ghost"
              leftIcon={<FontAwesomeIcon icon={faFilter} />}
            >
              <span className="ml-1">
                <FormattedMessage id="filters" />
              </span>
            </Button>
          </PrimaryFilterContainer>
        ),
        secondary: (
          <>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={searchQuery}
              onChange={onSearchChange}
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
              width="400px"
            />
            <CreateAccountButton
              variant="primary"
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              <span className="ml-1">
                <FormattedMessage id="new.account" />
              </span>
            </CreateAccountButton>
          </>
        ),
      }}
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faBuildings} size="sm" />}
          title={<FormattedMessage id="accounts.page.title" />}
        />
      }
      content={
        isFieldsLoading ? (
          <PageLoading />
        ) : (
          <Wrapper className="ag-theme-balham">
            <AccountsGrid {...gridOptions} onGridReady={onGridReady} />
          </Wrapper>
        )
      }
    />
  );
}

// Define the types
